import { useEffect, useState } from "react";

/**
 * Tracks the number of columns in the Bryntum TaskBoard by subscribing
 * to the columns store's add/remove/refresh/change events.
 *
 * Isolates Bryntum's event API behind a hook boundary so that version-sensitive
 * code lives in one place rather than inline in every consumer.
 *
 * @param taskBoardRef - React ref to the BryntumTaskBoard component
 * @param onError - Optional error callback (e.g. bugsnag.notify). Called when
 *   reading the column count fails after one retry.
 */
export function useColumnsCount(
   taskBoardRef: React.RefObject<any>,
   onError?: (error: unknown) => void,
): number {
   const [columnsCount, setColumnsCount] = useState<number>(() => {
      try {
         return taskBoardRef.current?.instance?.columns?.getCount({ all: true }) ?? 0;
      } catch {
         return 0;
      }
   });

   useEffect(() => {
      const taskboardInstance = taskBoardRef.current?.instance;
      const columnsStore = taskboardInstance?.columns;
      if (!columnsStore) return;

      const updateColumnsCount = () => {
         try {
            setColumnsCount(columnsStore.getCount({ all: true }));
         } catch (firstError) {
            try {
               const refreshedColumnsStore = taskBoardRef.current?.instance?.columns;
               if (!refreshedColumnsStore) {
                  throw firstError;
               }
               setColumnsCount(refreshedColumnsStore.getCount({ all: true }));
            } catch (secondError) {
               onError?.(secondError);
            }
         }
      };

      updateColumnsCount();
      columnsStore.on("add", updateColumnsCount);
      columnsStore.on("remove", updateColumnsCount);
      columnsStore.on("refresh", updateColumnsCount);
      columnsStore.on("change", updateColumnsCount);

      return () => {
         if (columnsStore && typeof columnsStore.off === "function") {
            columnsStore.off("add", updateColumnsCount);
            columnsStore.off("remove", updateColumnsCount);
            columnsStore.off("refresh", updateColumnsCount);
            columnsStore.off("change", updateColumnsCount);
         }
      };
   }, [taskBoardRef, onError]);

   return columnsCount;
}
